/**
 * supernovaSlots — the pure model shared by the Supernova slot rail and the
 * Strategy Automation cross-links. No React, no I/O, no network.
 *
 * ONE STAKED SUPERNOVA = ONE STRATEGY SLOT. That is the whole rule, and it has
 * exactly two inputs, which come from two different places and fail
 * independently:
 *
 *   staked count      the member's wallet + the staking contract (Supernova tab)
 *   automated strategies   the automation engine's saved pair allowlist
 *                          (Strategy Automation tab, GET /status.riskConfig)
 *
 * Either half can be unknown on its own, and an unknown half is NOT a zero: a
 * missing staked count must never render as "0 slots" and a missing allowlist
 * must never render as "nothing allocated". Every derived figure below is
 * therefore `null` unless the halves it needs are actually known, and the cell
 * list carries a distinct UNKNOWN kind so the rail can draw slots it can see
 * without claiming which ones are in use.
 *
 * "NEWEST" AUTOMATED STRATEGY — the ordering assumption behind pause-on-unstake.
 * TODO(backend, supervised): the engine exposes no timestamp for when a pair was
 * automated, so "newest" here means "last in the saved allowlist order". That is
 * the only ordering the API gives us. It drives a UI state only — the engine
 * enforces nothing about slots yet, and nothing in this file may claim it does.
 *
 * COMPLIANCE: nothing here is a return, a yield, a price, or a projection.
 * Slots are a count of unlocked automation seats, and XP never enters this file.
 */

export const SLOT_KIND = {
  /** Backed by a stake AND consumed by an automated strategy. */
  USED: "used",
  /** Backed by a stake and free for the member to allocate. */
  OPEN: "open",
  /** An automated strategy with no stake behind it any more. */
  UNBACKED: "unbacked",
  /** We know the slot exists but not whether anything is using it. */
  UNKNOWN: "unknown",
};

/** Engine wire key for a display pair: "BTC/USD" → "BTCUSD". */
export const wireKey = (pair) => String(pair || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/* Quote currencies the venue pairs actually use, longest first so "USDC" wins
   over "USD" when both would match. Only used to re-insert the slash in a
   normalized key — never to guess at a pair we don't recognise. */
const QUOTES = ["USDC", "USDT", "USD"];

/**
 * "BTCUSD" → "BTC/USD"; "BTC/USD" → unchanged; anything we can't split → as-is.
 * The engine normalizes allowlist entries, but a config saved from the UI can
 * still arrive with the slash, so both forms reach this code.
 */
export function displayPair(pair) {
  const raw = String(pair || "").trim();
  if (!raw) return "";
  if (raw.includes("/")) return raw;
  const key = wireKey(raw);
  const quote = QUOTES.find((q) => key.endsWith(q) && key.length > q.length + 1);
  return quote ? `${key.slice(0, -quote.length)}/${quote}` : raw;
}

/** "BTC/USD" → "BTC" — the base asset, for the narrow rail cells. */
export const shortPairLabel = (pair) => displayPair(pair).split("/")[0] || "";

/**
 * The strategies currently consuming slots, oldest → newest, from a GET /status
 * payload. Returns `null` — not `[]` — when the payload carries no allowlist,
 * because "the engine didn't tell us" and "nothing is automated" are different
 * facts and only one of them is safe to render.
 *
 * An allowlisted pair is what consumes a slot: it is the set the engine will
 * accept signals for. Duplicates (the same pair saved in both forms) collapse to
 * one slot, keeping the position of the first occurrence.
 */
export function automatedStrategiesFromStatus(status) {
  const list = status?.riskConfig?.pairAllowlist;
  if (!Array.isArray(list)) return null;
  const seen = new Set();
  const out = [];
  list.forEach((entry) => {
    const key = wireKey(entry);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(displayPair(entry));
  });
  return out;
}

const isCount = (n) => typeof n === "number" && Number.isFinite(n) && n >= 0;

/**
 * Derive the slot rail and the cross-link states.
 *
 * @param {Object} input
 * @param {number|null} [input.stakedCount]  staked Supernovas; null ⇒ unknown
 * @param {string[]|null} [input.strategies] automated strategies, oldest first;
 *        null ⇒ the engine's usage is unknown
 * @param {number|null} [input.usedCount]    slot usage when the COUNT is known
 *        but the names are not (a caller passing `slotsUsed` straight in).
 *        Ignored when `strategies` is supplied.
 * @returns {{
 *   stakedKnown: boolean, usageKnown: boolean,
 *   staked: number|null, used: number|null, free: number|null, unbacked: number|null,
 *   cells: Array<{key: string, kind: string, label: string, strategy: string|null}>,
 *   pausedStrategies: string[],
 * }}
 */
export function deriveSlots(input = {}) {
  const { stakedCount = null, strategies = null, usedCount = null } = input;

  const stakedKnown = isCount(stakedCount);
  const names = Array.isArray(strategies) ? strategies : null;
  const used = names ? names.length : isCount(usedCount) ? usedCount : null;
  const usageKnown = used !== null;

  const staked = stakedKnown ? stakedCount : null;
  const free = stakedKnown && usageKnown ? Math.max(0, staked - used) : null;
  const unbacked = stakedKnown && usageKnown ? Math.max(0, used - staked) : null;

  /* The strategies the stake no longer covers — the newest ones, because a
     member unstaking drops the most recently added seats first. Names only:
     with the allowlist unknown we know a count is over, not which pair it is. */
  const pausedStrategies = names && unbacked ? names.slice(staked) : [];

  const cells = [];
  const cell = (kind, label, strategy = null) =>
    cells.push({ key: `slot-${cells.length}`, kind, label, strategy });

  if (stakedKnown && !usageKnown) {
    /* We know how many slots the stake lit, not what is sitting in them. */
    for (let i = 0; i < staked; i += 1) cell(SLOT_KIND.UNKNOWN, "Slot");
  } else if (usageKnown) {
    const total = stakedKnown ? Math.max(staked, used) : used;
    for (let i = 0; i < total; i += 1) {
      const name = names ? names[i] || null : null;
      const inUse = i < used;
      if (!stakedKnown) {
        /* Usage without a staked count: we can name what is automated, but not
           say whether a stake is behind it. */
        cell(SLOT_KIND.USED, name ? shortPairLabel(name) : "In use", name);
      } else if (inUse && i < staked) {
        cell(SLOT_KIND.USED, name ? shortPairLabel(name) : "In use", name);
      } else if (i < staked) {
        cell(SLOT_KIND.OPEN, "Open");
      } else {
        cell(SLOT_KIND.UNBACKED, "Unbacked", name);
      }
    }
  }

  return { stakedKnown, usageKnown, staked, used, free, unbacked, cells, pausedStrategies };
}

export default deriveSlots;
